/**
 * 通用 antd-style 样式工厂
 *
 * 基于 antd token 系统，提供页面容器、卡片、表格、表单、操作栏，
 * 以及文字、Flex 布局、间距、颜色、溢出处理、面板、统计等通用工具类。
 *
 * 样式定义按关注点拆分为三个子文件:
 * - common-layout.ts: 页面容器、Flex、面板、列表项、定位等布局样式
 * - common-typography.ts: 文字颜色、字号、字重、标题、统计数字等排版样式
 * - common-utilities.ts: 间距、宽度、颜色、背景、边框、溢出、交互等工具类
 */

import { createStyles } from 'antd-style';
import { layoutStyles } from './common-layout';
import { typographyStyles } from './common-typography';
import { utilityStyles } from './common-utilities';
import type { CssFn, StyleDeps, TokenValues } from './style-types';

/**
 * 创建通用样式
 *
 * @example
 * ```tsx
 * const useCommonStyles = createCommonStyles();
 *
 * function MyPage() {
 *   const { styles } = useCommonStyles();
 *   return (
 *     <div className={styles.pageContainer}>
 *       <div className={styles.cardWrapper}>
 *         <ProTable className={styles.tableWrapper} />
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function createCommonStyles() {
  return createStyles(({ token, css }) => {
    // token 在 antd-style 中类型为 GlobalToken，但子文件仅需字符串插值，
    // 使用 TokenValues 简化类型（Record<string, string | number>）
    const deps: StyleDeps = {
      token: token as unknown as TokenValues,
      css: css as unknown as CssFn,
    };

    return {
      ...layoutStyles(deps),
      ...typographyStyles(deps),
      ...utilityStyles(deps),
    };
  });
}
